/**
 * Normalizes a string into a safe translation key by converting to camelCase.
 * Handles whitespace, special characters, snake_case, and preserves existing camelCase.
 *
 * @param value - The raw value to normalize (can be snake_case, plain text, or already camelCase)
 * @returns A normalized camelCase string safe for use as a translation key
 *
 * @example
 * // From plain text with spaces
 * normalizeTranslationKey('Work is not impaired or blocked')
 * // Returns: 'workIsNotImpairedOrBlocked'
 *
 * @example
 * // From snake_case (like backend slugs)
 * normalizeTranslationKey('applications_login_issue')
 * // Returns: 'applicationsLoginIssue'
 *
 * @example
 * // From single word
 * normalizeTranslationKey('Yes')
 * // Returns: 'yes'
 *
 * @example
 * // Already camelCase (preserved to avoid translation key mismatches)
 * normalizeTranslationKey('companyOwnedDevice')
 * // Returns: 'companyOwnedDevice'
 */
export function normalizeTranslationKey(value: string): string {
  const cleaned = value
    .replace(/[^\w\s]/g, '') // Remove special characters (keep alphanumeric, spaces, and underscores)
    .trim()
    .replace(/^_+|_+$/g, ''); // Remove leading and trailing underscores

  const hasSeparators = /[\s_]/.test(cleaned);

  if (hasSeparators) {
    return cleaned
      .toLowerCase()
      .replace(/[_\s]+(.)/g, (_, char) => char.toUpperCase())
      .replace(/^[A-Z]/, char => char.toLowerCase());
  }

  // No separators: already camelCase or single word — preserve casing, only ensure first char is lowercase
  return cleaned.replace(/^([A-Z])/, (_, char) => char.toLowerCase());
}

/**
 * Returns the category key for the top-level `categories` section.
 * Accepts slug or display name; both normalize to camelCase (e.g. "applications", "somethingElse").
 *
 * @param slugOrName - Backend slug or display name
 * @returns Key for `categories.{key}.name` / `categories.{key}.description`
 */
export function getCategoryTranslationKey(slugOrName: string): string {
  return normalizeTranslationKey(slugOrName);
}

/**
 * Constructs a translation key for backend-provided field content.
 * Automatically normalizes both the key and property values to ensure safe, consistent keys.
 *
 * Translation key format: `backend.{fieldType}.{normalizedKey}.{normalizedProperty}`
 *
 * @param fieldType - The type of field being translated (e.g., 'requestTypes', 'settings')
 * @param key - The unique key/identifier for the field (e.g., slug, name, or id) - will be normalized to camelCase
 * @param property - The specific property to translate (will be normalized to camelCase)
 * @returns The formatted translation key with normalized key and property
 *
 * @example
 * // For a request type translation with snake_case slug:
 * getBackendTranslationKey('requestTypes', 'applications_login_issue', 'name')
 * // Returns: 'backend.requestTypes.applicationsLoginIssue.name'
 *
 * @example
 * // For a settings field option with full string value:
 * getBackendTranslationKey('settings', 'urgency.options', 'Work is not impaired or blocked')
 * // Returns: 'backend.settings.urgency.options.workIsNotImpairedOrBlocked'
 *
 * @example
 * // For a settings field with snake_case key and property:
 * getBackendTranslationKey('settings', 'company_owned_device.options', 'Yes')
 * // Returns: 'backend.settings.companyOwnedDevice.options.yes'
 */
export function getBackendTranslationKey(
  fieldType: string,
  key: string,
  property: string
): string {
  // Normalize the key, but preserve dots for nested paths
  const normalizedKey = key
    .split('.')
    .map(part => normalizeTranslationKey(part))
    .join('.');
  const normalizedProperty = normalizeTranslationKey(property);
  return `backend.${fieldType}.${normalizedKey}.${normalizedProperty}`;
}
